import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("dj_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// A short-lived, download-only token is cached and reused for every file link on a page
// (thumbnails + click-to-open) until it is nearly expired, then transparently refreshed.
let _dlToken = { value: "", exp: 0 };

export const getDownloadToken = async () => {
  if (_dlToken.value && Date.now() < _dlToken.exp) return _dlToken.value;
  const { data } = await api.get("/auth/download-token");
  _dlToken = {
    value: data.token,
    exp: Date.now() + Math.max(0, (data.expires_in || 120) - 20) * 1000,
  };
  return _dlToken.value;
};

export const clearDownloadToken = () => {
  _dlToken = { value: "", exp: 0 };
};

/** Absolute URL to a stored file, carrying a short-lived download-only token. */
export const fileUrl = async (fileId) =>
  `${API}/files/${fileId}?auth=${encodeURIComponent(await getDownloadToken())}`;

/** Open any download endpoint (file / statement / receipt PDF) in a new tab without
 *  leaking the session token. The blank tab is opened synchronously so pop-up blockers
 *  allow it, then its address is filled in once the short-lived token arrives. */
export const openDownload = async (path) => {
  const win = window.open("", "_blank");
  try {
    const t = await getDownloadToken();
    const sep = path.includes("?") ? "&" : "?";
    const url = `${API}${path}${sep}auth=${encodeURIComponent(t)}`;
    if (win) win.location.href = url;
    else window.open(url, "_blank");
  } catch (e) {
    if (win) win.close();
    throw e;
  }
};

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const money = (v) =>
  (Number(v) || 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" });

// The shop trades in Melbourne — never show a browser/UTC date or time.
export const SHOP_TZ = "Australia/Melbourne";

/** The API sends UTC timestamps with no zone marker ("2026-06-30T04:55:57.689000").
 *  A browser reads those as its own local time, which put every date and time in the
 *  app 10 hours out for anyone sitting in Melbourne. Anything without a zone is UTC. */
export const asDate = (v) => {
  if (v instanceof Date) return v;
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T[\d:.]+$/.test(v.trim()))
    return new Date(`${v.trim()}Z`);
  return new Date(v);
};

export const shopDateTime = (v) =>
  v
    ? asDate(v).toLocaleString("en-AU", {
        timeZone: SHOP_TZ,
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : "-";

export const shopDate = (v) =>
  v
    ? asDate(v).toLocaleDateString("en-AU", {
        timeZone: SHOP_TZ,
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "-";

export const shopTime = (v) =>
  v
    ? asDate(v).toLocaleTimeString("en-AU", {
        timeZone: SHOP_TZ,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : "-";

/** Whole minutes since a UTC timestamp from the API, never negative. */
export const minutesSince = (v) =>
  v ? Math.max(0, Math.round((Date.now() - asDate(v).getTime()) / 60000)) : 0;

/** Today in Melbourne as YYYY-MM-DD (or YYYY-MM when monthOnly). */
export const shopToday = (monthOnly = false) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: SHOP_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  return monthOnly ? parts.slice(0, 7) : parts;
};

export const roleLabel = (r) =>
  ({ it_admin: "IT Administrator", admin: "Admin", manager: "Manager", staff: "Staff" }[r] ||
    r ||
    "");

export const DOC_LABELS = {
  tax_invoice: "Tax Invoice",
  gold_bullion: "Gold Bullion Invoice",
  loan: "Loan Agreement",
  buying_contract: "Buying Contract",
};

export const NUMBER_LABELS = {
  tax_invoice: "INVOICE NO",
  gold_bullion: "INVOICE NO",
  loan: "LOAN NO",
  buying_contract: "BUYING CONT NO",
};

export const BRANCH_INFO = {
  DANDENONG: "Shop 3, 55 Foster Street, Dandenong VIC 3175  |  Ph: 03 9793 1000",
  TRUGANINA: "Shop 12, Woodlea Town Centre, Truganina VIC 3029  |  Ph: 03 8390 2000",
};
